import React, { useState, useCallback, useEffect } from "react";
import { View } from "react-native";
import { extractApiError } from "@/lib/api/errors";
import { Modal } from "@/components/ui/Modal";
import { AppTextInput } from "@/components/ui/TextInput";
import { AppTextArea } from "@/components/ui/AppTextArea";
import { Button } from "@/components/ui/Button";
import { SelectField } from "@/components/ui/SelectField";
import { SuccessState } from "@/components/ui/SuccessState";
import { ErrorState } from "@/components/ui/ErrorState";
import { AppText } from "@/components/ui/AppText";
import type { AddLocalizationKeyPayload, ContentType } from "@/lib/api/localization";
import { useI18n } from "@/context/I18nContext";
import { Feather } from "@expo/vector-icons";
import { Icon } from "@/components/ui/Icon";

export interface AddContentModalProps {
  visible: boolean;
  onDismiss: () => void;
  /**
   * Called with the new key payload.
   * API wiring is tracked in a future issue.
   */
  onSubmit?: (payload: AddLocalizationKeyPayload) => void;
}

type ModalState = "form" | "success" | "error";

/**
 * Modal for adding a new localization key.
 *
 * Layout: Key Title + Content Type (side by side) → Translations header →
 * EN / AR / ZH-CN text areas → info hint → Submit.
 *
 * Matches Figma "Staff / Localization / Add New Content" modal (node 13552-46013).
 */
export function AddContentModal({ visible, onDismiss, onSubmit }: AddContentModalProps) {
  const { t } = useI18n();
  const [modalState, setModalState] = useState<ModalState>("form");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [keySlug, setKeySlug] = useState("");
  const [contentType, setContentType] = useState<ContentType | null>(null);
  const [en, setEn] = useState("");
  const [ar, setAr] = useState("");
  const [zhCn, setZhCn] = useState("");

  // Only "other" requires a key slug — industries/categories use generated IDs.
  const requiresKeySlug = contentType === "other" || contentType === null;

  const contentTypeOptions = [
    {
      label: t("staff.localization.addContent.contentTypeCategories"),
      value: "categories" as ContentType,
    },
    {
      label: t("staff.localization.addContent.contentTypeIndustries"),
      value: "industries" as ContentType,
    },
    {
      label: t("staff.localization.addContent.contentTypeOther"),
      value: "other" as ContentType,
    },
  ];

  useEffect(() => {
    if (visible) {
      setModalState("form");
      setErrorMessage(null);
      setKeySlug("");
      setContentType(null);
      setEn("");
      setAr("");
      setZhCn("");
    }
  }, [visible]);

  const handleDismiss = useCallback(() => {
    setModalState("form");
    onDismiss();
  }, [onDismiss]);

  const canSubmit =
    (!requiresKeySlug || keySlug.trim() !== "") &&
    contentType !== null &&
    (en.trim() !== "" || ar.trim() !== "" || zhCn.trim() !== "");

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || contentType === null) return;
    try {
      await Promise.resolve(
        onSubmit?.({
          key: keySlug.trim(),
          contentType: contentType as ContentType,
          en: en.trim() || undefined,
          "zh-CN": zhCn.trim() || undefined,
          ar: ar.trim() || undefined,
        })
      );
      setModalState("success");
    } catch (err) {
      setErrorMessage(extractApiError(err, ""));
      setModalState("error");
    }
  }, [canSubmit, keySlug, contentType, en, zhCn, ar, onSubmit]);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      title={modalState === "form" ? "staff.localization.addContent.title" : null}
      useScrollView={modalState === "form"}
      footer={
        modalState === "form" ? (
          <View className="flex-row justify-end gap-3">
            <Button
              label={"staff.localization.addContent.cancel"}
              variant="tertiary"
              onPress={handleDismiss}
            />
            <Button
              label={"staff.localization.addContent.submit"}
              variant="primary"
              onPress={handleSubmit}
              disabled={!canSubmit}
            />
          </View>
        ) : null
      }
    >
      {modalState === "success" ? (
        <SuccessState
          title={"staff.localization.addContent.success"}
          description={"staff.localization.addContent.successBody"}
          onPrimaryAction={handleDismiss}
          primaryActionLabel={"common.close"}
          buttonVariant="primary"
          className="py-6 px-0"
        />
      ) : modalState === "error" ? (
        <ErrorState
          title="staff.localization.addContent.error"
          description="staff.localization.addContent.errorBody"
          descriptionText={errorMessage || undefined}
          onRetry={() => setModalState("form")}
          retryLabel="errorState.retry"
          onSecondaryAction={handleDismiss}
          secondaryActionLabel="common.cancel"
        />
      ) : (
        <>
          {/* Key Title + Content Type — side by side on wider screens, stacked on narrow */}
          <View className="w-full flex-col tablet:flex-row gap-4 mb-4">
            {requiresKeySlug && (
              <View className="flex-1">
                <AppTextInput
                  label={"staff.localization.addContent.keyLabel"}
                  placeholder={"staff.localization.addContent.keyPlaceholder"}
                  value={keySlug}
                  onChangeText={setKeySlug}
                  containerClassName="mb-0"
                  required
                />
              </View>
            )}
            <View className="flex-1">
              <SelectField
                label={"staff.localization.addContent.contentTypeLabel"}
                placeholder={"staff.localization.addContent.contentTypePlaceholder"}
                options={contentTypeOptions}
                value={contentType ?? undefined}
                onChange={(v) => setContentType(v as ContentType)}
                required
              />
            </View>
          </View>

          {/* Translations section header */}
          <AppText weight="semibold" className="text-base text-dark-text-50 mb-3">
            {t("staff.localization.addContent.translationsHeader")}
          </AppText>

          {/* English Translation */}
          <AppTextArea
            label={"staff.localization.addContent.englishLabel"}
            value={en}
            onChangeText={setEn}
            numberOfLines={3}
            containerClassName="mb-4"
          />

          {/* Arabic Translation */}
          <AppTextArea
            label={"staff.localization.addContent.arabicLabel"}
            value={ar}
            onChangeText={setAr}
            numberOfLines={3}
            isRTL
            containerClassName="mb-4"
          />

          {/* Chinese Translation */}
          <AppTextArea
            label={"staff.localization.addContent.chineseLabel"}
            value={zhCn}
            onChangeText={setZhCn}
            numberOfLines={3}
            containerClassName="mb-4"
          />

          {/* Info banner */}
          <View className="w-full mb-6 bg-blue-5 flex-row items-center gap-2 rounded-lg px-4 py-3">
            <Icon as={Feather} name="info" size={14} className="text-blue-50" />

            <AppText className="text-sm text-blue-50">
              {t("staff.localization.addContent.infoText")}
            </AppText>
          </View>
        </>
      )}
    </Modal>
  );
}
